import { CreditSubmissionDecisionViewModel, RateConfigurationType } from '@trnsact/trnsact-shared-types';

import { CreateProposalFinanceExistingOptions, FinanceProgramFormValues, Structures } from '../types';

class DecisionMapper {
  public processDecisionToDeskingForm(
    formDefaultValues: FinanceProgramFormValues,
    entity: CreditSubmissionDecisionViewModel
  ): FinanceProgramFormValues {
    const dealerMarkup = this.extractDealerMarkup(entity);
    const specifyRates =
      entity.rateConfiguration === RateConfigurationType.SingleRate ? [] : this.mapSpecifyRates(entity);
    const specifyRate = entity.rateConfiguration === RateConfigurationType.RatePerTerm;
    const subsidyCapConfiguration = this.mapSubsidyCapConfiguration(formDefaultValues, entity);

    return {
      ...formDefaultValues,
      submission: {
        ...formDefaultValues.submission,
        maxApprovalAmount: entity.maxApprovalAmount ?? null,
      },
      financeQuote: {
        ...formDefaultValues.financeQuote,
        specifyRate,
        specifyRates,
        subsidyCapConfiguration,
        startDate: entity.startDate ?? '',
        commonRate: entity.interestRate ?? 0,
        markupRate: entity.customerRate ?? null,
        program: entity.financeProgramId || null,
        residual: entity.residualPercentage ?? null,
        dealerMarkup: dealerMarkup?.markupPercentage ?? null,
        configureSubsidyCap: !!entity.subsidyCapConfiguration,
        advancePayment: String(entity.numAdvancePayments ?? 0),
        minimumRequiredDownPayment: entity.minDownPayment ?? 0,
        daysToFirstPayment: String(entity.daysToFirstPayment ?? 0),

        ...(entity.rateType ? { rateType: entity.rateType } : {}),
        ...(entity.financeProgramType ? { programType: entity.financeProgramType } : {}),
        fee: (entity.fees ?? []).map(fee => ({ ...fee, identifier: fee?.identifier ?? '' })),

        terms: (entity.termRates ?? []).map(({ term }) => String(term)),
        structure: dealerMarkup ? Structures.Markup : subsidyCapConfiguration ? Structures.Subsidy : Structures.None,
        createProposalWithExistingProgram: entity.financeProgramId
          ? CreateProposalFinanceExistingOptions.Yes
          : CreateProposalFinanceExistingOptions.No,
      },
    };
  }

  private extractDealerMarkup(entity: CreditSubmissionDecisionViewModel) {
    return entity.markups?.find(m => m.identifier === 'dealer_markup');
  }

  private mapSpecifyRates(entity: CreditSubmissionDecisionViewModel) {
    return (entity.termRates ?? []).map(({ term, rate }) => ({ rate, term: String(term) }));
  }

  private mapSubsidyCapConfiguration(
    formDefaultValues: FinanceProgramFormValues,
    entity: CreditSubmissionDecisionViewModel
  ) {
    if (!entity.subsidyCapConfiguration) {
      return formDefaultValues.financeQuote.subsidyCapConfiguration;
    }

    return {
      lenderName: entity.subsidyCapConfiguration.lenderName,
      subsidyCapType: entity.subsidyCapConfiguration.subsidyCapType,
      maxSubsidyAmount: entity.subsidyCapConfiguration.maxSubsidyAmount ?? 0,
      netInvoiceAmount: entity.subsidyCapConfiguration.netInvoiceAmount ?? 0,
      netInvoicePercentage: entity.subsidyCapConfiguration.netInvoicePercentage ?? 0,
      lenderMarketRates: (entity.subsidyCapConfiguration.lenderMarketRates ?? [])
        .filter((r): r is NonNullable<typeof r> => r != null)
        .map(r => ({ term: r.term, rate: r.rate })),
    };
  }
}

export const deskingDecisionMapper = new DecisionMapper();
